import dayjs from 'dayjs';

import { ChartSignal, Series, SeriesData, StatusTypes } from 'api/traps/types';

export function getCellValue(signals: ChartSignal[], isFuture: boolean): number {
  if (signals.length === 0) {
    return isFuture ? -1000000 : 0;
  }
  const hasCaught = signals.some(
    signal => signal.approv_status === StatusTypes.APPROVED_MICE || signal.approv_status === StatusTypes.CONSUMPTION,
  );

  return hasCaught
    ? signals.filter(
        signal =>
          signal.approv_status === StatusTypes.APPROVED_MICE || signal.approv_status === StatusTypes.CONSUMPTION,
      ).length
    : -signals.filter(
        signal =>
          signal.approv_status !== StatusTypes.APPROVED_MICE && signal.approv_status !== StatusTypes.CONSUMPTION,
      ).length;
}

export function drawWeekSeparators(chartContext: any, selectedMonth: string | null, currentYear: string) {
  if (!chartContext?.el || !selectedMonth) return;

  const svg = chartContext.el.querySelector('.apexcharts-inner');
  if (!svg) return;

  svg.querySelectorAll('.week-separator-line').forEach((el: any) => el.remove());

  const seriesEls = chartContext.el.querySelectorAll('.apexcharts-series');
  if (!seriesEls.length) return;

  let x1 = Infinity;
  let x2 = -Infinity;
  const yValues: number[] = [];

  seriesEls.forEach((seriesEl: any) => {
    const rects = seriesEl.querySelectorAll('.apexcharts-heatmap-rect');

    rects.forEach((rect: any) => {
      const x = Number(rect.getAttribute('x') ?? 0);
      const width = Number(rect.getAttribute('width') ?? 0);
      const y = Number(rect.getAttribute('y') ?? 0);

      x1 = Math.min(x1, x);
      x2 = Math.max(x2, x + width);

      if (!Number.isNaN(y)) {
        yValues.push(y);
      }
    });
  });

  if (!yValues.length) return;

  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  seriesEls.forEach((seriesEl: any, rowIndex: number) => {
    const seriesName = String(chartContext.w.config.series[rowIndex]?.name ?? '').trim();
    const day = Number(seriesName);

    if (!day || Number.isNaN(day)) return;

    const date = dayjs(`${currentYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`);

    if (date.day() !== 0) return;

    const firstRect = seriesEl.querySelector('.apexcharts-heatmap-rect');
    if (!firstRect) return;

    const rawY = Number(firstRect.getAttribute('y') ?? 0);

    const lineY = minY + maxY - rawY;

    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y1', String(lineY));
    line.setAttribute('y2', String(lineY));
    line.setAttribute('stroke', '#fff');
    line.setAttribute('stroke-width', '3');
    line.setAttribute('class', 'week-separator-line');

    svg.appendChild(line);
  });
}

export function simulateCalendar(year: number, selectedMonth: string | null, signals: ChartSignal[]): Series[] {
  const now = dayjs();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const formatSignalString = (s: any) => {
    const timeStr = dayjs(s.date_time).format('DD-MM-YYYY HH:mm:ss');
    const trapType = s.trap_type ?? s.type ?? (['ACTIVITY', 'CONSUMPTION'].includes(s.approv_status) ? 'srbm' : 'mct');

    let enterOrBait = '';

    if (s.enter !== undefined && s.enter !== null) {
      enterOrBait = s.enter;
    } else if (s.approv_status === 'ACTIVITY') {
      enterOrBait = '0';
    } else if (s.eaten_bait !== undefined && s.eaten_bait !== null) {
      const rawBait = Number(s.eaten_bait);

      if (rawBait < 0.01) {
        enterOrBait = '0';
      } else {
        enterOrBait = rawBait.toFixed(2);
      }
    } else if (trapType === 'srbm') {
      enterOrBait = '0';
    }

    return `${timeStr} ${enterOrBait} ${s.approv_status} ${trapType}`.replace(/\s+/g, ' ').trim();
  };

  if (selectedMonth !== null) {
    const month = parseInt(selectedMonth, 10);
    const daysInMonth = dayjs(`${year}-${month}`).daysInMonth();
    const series: Series[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dayLabel = `${day}`;
      const data: SeriesData[] = [];

      for (let hour = 0; hour < 24; hour++) {
        const cellDate = dayjs(`${year}-${month}-${day} ${hour}:00`);
        const isFuture = cellDate.isAfter(now);

        const signalsForHour = signals.filter(signal => {
          const dt = dayjs(signal.date_time);
          return dt.year() === year && dt.month() + 1 === month && dt.date() === day && dt.hour() === hour;
        });

        const cellValue = getCellValue(signalsForHour, isFuture);
        const captureCount = Math.round(
          signalsForHour.filter(
            s => s.approv_status === StatusTypes.APPROVED_MICE || s.approv_status === StatusTypes.CONSUMPTION,
          ).length,
        );
        const nonCaptureCount = Math.round(signalsForHour.length - captureCount);
        const eatenBaitTotal = Number(
          signalsForHour
            .filter(s => s.approv_status === StatusTypes.CONSUMPTION)
            .reduce((sum, s) => sum + (s.eaten_bait || 0), 0)
            .toFixed(2),
        );

        data.push({
          x: `${hour}:00`,
          y: cellValue,
          captureCount,
          nonCaptureCount,
          eaten_bait: eatenBaitTotal,
          times: signalsForHour.map(formatSignalString),
        });
      }

      series.push({ name: dayLabel, data });
    }

    return series;
  } else {
    // === Year view: y = months, x = days ===
    const maxDays = 31;
    const series: Series[] = [];

    for (let month = 1; month <= 12; month++) {
      const monthLabel = monthNames[month - 1]; // Y-axis = month
      const daysInMonth = dayjs(`${year}-${month}`).daysInMonth();

      const data: SeriesData[] = [];

      for (let day = 1; day <= maxDays; day++) {
        const cellDate = dayjs(`${year}-${month}-${day}`);
        const isValid = day <= daysInMonth;
        const isFuture = cellDate.isAfter(now);

        const signalsForDay = isValid
          ? signals.filter(signal => {
              const dt = dayjs(signal.date_time);
              return dt.year() === year && dt.month() + 1 === month && dt.date() === day;
            })
          : [];

        const cellValue = getCellValue(signalsForDay, isFuture);
        const captureCount = Math.round(
          signalsForDay.filter(
            s => s.approv_status === StatusTypes.APPROVED_MICE || s.approv_status === StatusTypes.CONSUMPTION,
          ).length,
        );
        const nonCaptureCount = Math.round(signalsForDay.length - captureCount);
        const eatenBaitTotal = Number(
          signalsForDay
            .filter(s => s.approv_status === StatusTypes.CONSUMPTION)
            .reduce((sum, s) => sum + (s.eaten_bait || 0), 0)
            .toFixed(2),
        );
        data.push({
          x: `${day}`,
          y: cellValue,
          captureCount,
          nonCaptureCount,
          eaten_bait: eatenBaitTotal,
          times: signalsForDay.map(formatSignalString),
        });
      }

      series.push({ name: monthLabel, data });
    }

    return series;
  }
}
